import type {BoxProps} from '@mui/material/Box';
import Box from '@mui/material/Box';
import type {CheckboxProps} from '@mui/material/Checkbox';
import Checkbox from '@mui/material/Checkbox';
import type {FormControlProps} from '@mui/material/FormControl';
import FormControl from '@mui/material/FormControl';
import type {FormControlLabelProps} from '@mui/material/FormControlLabel';
import FormControlLabel from '@mui/material/FormControlLabel';
import type {FormGroupProps} from '@mui/material/FormGroup';
import FormGroup from '@mui/material/FormGroup';
import type {FormHelperTextProps} from '@mui/material/FormHelperText';
import type {FormLabelProps} from '@mui/material/FormLabel';
import FormLabel from '@mui/material/FormLabel';
import {ReactNode} from 'react';
import {Controller, useFormContext} from 'react-hook-form';

import {HelperText} from './help-text';

// ----------------------------------------------------------------------

type RHFCheckboxProps = Omit<FormControlLabelProps, 'control'> & {
  name: string;
  helperText?: ReactNode;
  slotProps?: {
    wrapper?: BoxProps;
    checkbox?: CheckboxProps;
    helperText?: FormHelperTextProps;
  };
};

export const RHFCheckbox = ({sx, name, label, slotProps, helperText, ...other}: RHFCheckboxProps) => {
  const {control} = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      render={({field, fieldState: {error}}) => (
        <Box {...slotProps?.wrapper}>
          <FormControlLabel
            label={label}
            control={
              <Checkbox
                {...field}
                checked={field.value}
                {...slotProps?.checkbox}
                inputProps={{
                  id: `${name}-checkbox`,
                  ...(!label && {'aria-label': `${name} checkbox`}),
                  ...slotProps?.checkbox?.inputProps,
                }}
              />
            }
            sx={[{mx: 0}, ...(Array.isArray(sx) ? (sx ?? []) : [sx])]}
            {...other}
          />

          <HelperText {...slotProps?.helperText} errorMessage={error?.message} helperText={helperText} />
        </Box>
      )}
    />
  );
};

// ----------------------------------------------------------------------

type RHFMultiCheckboxProps = FormGroupProps & {
  name: string;
  label?: string;
  helperText?: ReactNode;
  options: {label: string; value: string}[];
  slotProps?: {
    wrapper?: FormControlProps;
    checkbox?: CheckboxProps;
    formLabel?: FormLabelProps;
    helperText?: FormHelperTextProps;
  };
};

export const RHFMultiCheckbox = ({name, label, options, slotProps, helperText, ...other}: RHFMultiCheckboxProps) => {
  const {control} = useFormContext();

  const getSelected = (selectedItems: string[], item: string) =>
    selectedItems.includes(item) ? selectedItems.filter((value) => value !== item) : [...selectedItems, item];

  return (
    <Controller
      name={name}
      control={control}
      render={({field, fieldState: {error}}) => (
        <FormControl component="fieldset" {...slotProps?.wrapper}>
          {label && (
            <FormLabel
              component="legend"
              {...slotProps?.formLabel}
              sx={[
                {mb: 1, typography: 'body2'},
                ...(Array.isArray(slotProps?.formLabel?.sx)
                  ? (slotProps?.formLabel?.sx ?? [])
                  : [slotProps?.formLabel?.sx]),
              ]}
            >
              {label}
            </FormLabel>
          )}

          <FormGroup {...other}>
            {options.map((option) => (
              <FormControlLabel
                key={option.value}
                control={
                  <Checkbox
                    checked={field.value.includes(option.value)}
                    onChange={() => field.onChange(getSelected(field.value, option.value))}
                    {...slotProps?.checkbox}
                    inputProps={{
                      id: `${option.label}-checkbox`,
                      ...(!option.label && {'aria-label': `${option.label} checkbox`}),
                      ...slotProps?.checkbox?.inputProps,
                    }}
                  />
                }
                label={option.label}
              />
            ))}
          </FormGroup>

          <HelperText {...slotProps?.helperText} disableGutters errorMessage={error?.message} helperText={helperText} />
        </FormControl>
      )}
    />
  );
};
